'use client'

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdAdd,
    MdEdit,
    MdDelete,
    MdSearch,
    MdAttachMoney,
    MdToggleOn,
    MdToggleOff,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getCoparticipationRules,
    deleteCoparticipationRule,
    updateCoparticipationRule,
    CoparticipationRule,
    COPARTICIPATION_TYPE_LABELS,
} from '@/lib/api/billing/coparticipation-api'

export default function CoparticipationPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'billing',
        'coparticipation'
    )

    const [rules, setRules] = useState<CoparticipationRule[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [filterActive, setFilterActive] = useState<boolean | undefined>(
        undefined
    )

    useEffect(() => {
        if (user?.companyId) {
            loadRules()
        }
    }, [user?.companyId, filterActive])

    const loadRules = async () => {
        setLoading(true)
        try {
            const response = await getCoparticipationRules({
                active: filterActive,
            })
            if (response.success) {
                setRules(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar regras')
            }
        } catch (error) {
            toast.error('Erro ao carregar regras')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return

        try {
            const response = await deleteCoparticipationRule(deleteId)
            if (response.success) {
                toast.success('Regra excluída com sucesso')
                setDeleteId(null)
                loadRules()
            } else {
                toast.error(response.error || 'Erro ao excluir regra')
            }
        } catch (error) {
            toast.error('Erro ao excluir regra')
        }
    }

    const handleToggleActive = async (rule: CoparticipationRule) => {
        try {
            const response = await updateCoparticipationRule(rule.id, {
                active: !rule.active,
            })
            if (response.success) {
                toast.success(
                    rule.active ? 'Regra desativada' : 'Regra ativada'
                )
                loadRules()
            } else {
                toast.error(response.error || 'Erro ao atualizar regra')
            }
        } catch (error) {
            toast.error('Erro ao atualizar regra')
        }
    }

    const formatValue = (rule: CoparticipationRule) => {
        switch (rule.type) {
            case 'PERCENTAGE':
                return `${rule.percentageValue}%`
            case 'FIXED_VALUE':
                return formatCurrency(rule.fixedValue || 0)
            case 'MIXED':
                return `${rule.percentageValue}% + ${formatCurrency(rule.fixedValue || 0)}`
            case 'CEILING':
                return `Teto: ${formatCurrency(rule.ceilingValue || 0)}`
            case 'DEDUCTIBLE':
                return `Franquia: ${formatCurrency(rule.deductibleValue || 0)}`
            default:
                return '-'
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    const filteredRules = rules.filter(rule => {
        if (!searchTerm) return true
        const search = searchTerm.toLowerCase()
        return (
            rule.name.toLowerCase().includes(search) ||
            rule.healthPlan?.name?.toLowerCase().includes(search)
        )
    })

    const getAppliesTo = (rule: CoparticipationRule) => {
        const applies: string[] = []
        if (rule.appliesToConsultation) applies.push('Consultas')
        if (rule.appliesToExam) applies.push('Exames')
        if (rule.appliesToOrder) applies.push('Procedimentos')
        if (rule.appliesToHospitalization) applies.push('Internações')
        return applies.length > 0 ? applies.join(', ') : 'Nenhum'
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <MdAttachMoney className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Regras de Coparticipação
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Configure regras de coparticipação por convênio
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        onClick={() => navigate('/billing/coparticipation/new')}
                        className="btn btn-secondary gap-2"
                    >
                        <MdAdd className="text-lg" />
                        Nova Regra
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 flex-wrap">
                <div className="relative flex-1 max-w-md">
                    <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                    <input
                        type="text"
                        placeholder="Buscar por nome ou convenio..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="input input-bordered w-full pl-10"
                    />
                </div>
                <select
                    className="select select-bordered"
                    value={
                        filterActive === undefined
                            ? ''
                            : filterActive
                              ? 'active'
                              : 'inactive'
                    }
                    onChange={e => {
                        const val = e.target.value
                        setFilterActive(
                            val === '' ? undefined : val === 'active'
                        )
                    }}
                >
                    <option value="">Todas</option>
                    <option value="active">Ativas</option>
                    <option value="inactive">Inativas</option>
                </select>
            </div>

            {/* Table */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <span className="loading loading-spinner loading-lg"></span>
                        </div>
                    ) : filteredRules.length === 0 ? (
                        <div className="text-center py-12 text-base-content/60">
                            <MdAttachMoney className="text-5xl mx-auto mb-3 opacity-40" />
                            <p className="text-lg">
                                Nenhuma regra de coparticipação encontrada...
                            </p>
                            {canCreate && (
                                <button
                                    onClick={() =>
                                        navigate('/billing/coparticipation/new')
                                    }
                                    className="btn btn-secondary btn-sm mt-4 gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    Criar Regra
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Convenio</th>
                                        <th>Tipo</th>
                                        <th>Valor</th>
                                        <th>Aplicação</th>
                                        <th>Status</th>
                                        {(canUpdate || canDelete) && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRules.map(rule => (
                                        <tr key={rule.id} className="hover">
                                            <td className="font-semibold">
                                                {rule.name}
                                            </td>
                                            <td>
                                                {rule.healthPlan?.name || '-'}
                                            </td>
                                            <td>
                                                <span className="badge badge-ghost">
                                                    {
                                                        COPARTICIPATION_TYPE_LABELS[
                                                            rule.type
                                                        ]
                                                    }
                                                </span>
                                            </td>
                                            <td className="font-mono">
                                                {formatValue(rule)}
                                            </td>
                                            <td className="text-sm text-base-content/70 max-w-48 truncate">
                                                {getAppliesTo(rule)}
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() =>
                                                        handleToggleActive(rule)
                                                    }
                                                    className={`badge gap-1 cursor-pointer ${rule.active ? 'badge-success' : 'badge-ghost'}`}
                                                    disabled={!canUpdate}
                                                >
                                                    {rule.active ? (
                                                        <>
                                                            <MdToggleOn className="text-lg" />
                                                            Ativa
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MdToggleOff className="text-lg" />
                                                            Inativa
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            {(canUpdate || canDelete) && (
                                                <td>
                                                    <div className="flex items-center gap-1 justify-end">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/billing/coparticipation/${rule.id}/edit`
                                                                    )
                                                                }
                                                                className="btn btn-ghost btn-sm"
                                                            >
                                                                <MdEdit className="text-lg" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() =>
                                                                    setDeleteId(
                                                                        rule.id
                                                                    )
                                                                }
                                                                className="btn btn-ghost btn-sm text-error"
                                                            >
                                                                <MdDelete className="text-lg" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Modal */}
            {deleteId && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">
                            Confirmar exclusão
                        </h3>
                        <p className="py-4">
                            Tem certeza que deseja excluir esta regra de
                            coparticipação? Esta ação não pode ser desfeita.
                        </p>
                        <div className="modal-action">
                            <button
                                onClick={() => setDeleteId(null)}
                                className="btn btn-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDelete}
                                className="btn btn-error"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setDeleteId(null)}
                    ></div>
                </div>
            )}
        </div>
    )
}
